import React, { useCallback } from 'react';
import { Input } from '@amzn/awsui-components-console';

interface SearchBarProps {
  readonly queryText: string;
  readonly onQueryChange: (query: string) => void;
  readonly onSearch: () => void;
  /**
   * Whether the current state satisfies the minimum search criteria. Computed by
   * `SearchPage` (see `hasMinimumSearchCriteria`) and used here to gate the ONE
   * submit path this component owns — the Enter key. The Search button honours the
   * same flag in the panel footer.
   */
  readonly canSearch: boolean;
  /**
   * Id of the panel footer's blocked-reason message, or `undefined` when there is
   * no reason to show.
   *
   * The reason is rendered in the footer (`SearchPanelFooter`) rather than on a
   * `FormField` around this input, so the association FormField used to make for
   * free is made explicitly here instead — see the note in `SearchPage`. Without
   * it the only explanation for a refused search would be spatially adjacent to
   * the Search button and programmatically attached to nothing.
   */
  readonly ariaDescribedby?: string;
}

/**
 * The free-text query field — the full width of the search panel, with no controls
 * beside it. Submission lives in the panel footer; the Enter key below is the
 * keyboard equivalent of that button and honours the identical gate.
 */
const SearchBar: React.FC<SearchBarProps> = ({
  queryText,
  onQueryChange,
  onSearch,
  canSearch,
  ariaDescribedby,
}) => {
  const handleKeyDown = useCallback(
    (event: CustomEvent<{ key: string }>) => {
      /*
       * Enter is a second submit path and must honour the same gate as the
       * button — a disabled button alone would leave the keyboard route open.
       * Typing is never blocked; only submission is.
       */
      if (event.detail.key === 'Enter' && canSearch) {
        onSearch();
      }
    },
    [onSearch, canSearch],
  );

  return (
    <Input
      value={queryText}
      onChange={({ detail }) => onQueryChange(detail.value)}
      onKeyDown={handleKeyDown}
      placeholder="Search by String ID, source text or translated text..."
      type="search"
      ariaLabel="Search query"
      ariaDescribedby={ariaDescribedby}
    />
  );
};

export default SearchBar;
